import { Card, CardContent, Box, Typography, IconButton } from '@mui/material';
import {
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@utils/numberFormatters';
import { useTheme } from '@mui/material/styles';
import { useResponsive } from '../../../shared/hooks/useResponsive';
import type { WorkOrderPart } from '../../types/parts.types';

type Props = {
  part: WorkOrderPart;
  onClick?: () => void;
  onDelete?: () => void;
  hidePricing?: boolean;
};

export const PartCard = ({
  part,
  onClick,
  onDelete,
  hidePricing = false
}: Props) => {
  const { t } = useTranslation('parts');
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const isDarkMode = theme.palette.mode === 'dark';

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        border: theme => `1px solid ${theme.palette.divider}`,
        boxShadow: 0,
        position: 'relative',
        height: isMobile ? 'auto' : 80,
        minHeight: isMobile ? 100 : 80,
        display: 'flex',
        flexDirection: 'column',
        '&:hover': onClick
          ? {
              backgroundColor: theme => theme.palette.action.hover,
              borderColor: 'primary.main'
            }
          : {}
      }}
    >
      {onDelete && (
        <IconButton
          onClick={handleDelete}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            color: isDarkMode ? 'rgba(244, 67, 54, 0.6)' : 'error.main',
            '&:hover': {
              backgroundColor: 'rgba(244, 67, 54, 0.08)',
              color: isDarkMode ? 'rgba(244, 67, 54, 0.8)' : 'error.dark'
            }
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}

      <CardContent
        sx={{
          p: 2,
          '&:last-child': { pb: 2 },
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 3,
            pr: onDelete ? 4 : 0,
            width: '100%'
          }}
        >
          <Box
            sx={{
              flex: '0 0 auto',
              minWidth: 80,
              overflow: 'hidden'
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                mb: !isMobile && part.description ? 0.5 : 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={part.partNumber || 'Unknown Part'}
            >
              {part.partNumber || 'Unknown Part'}
            </Typography>
            {!isMobile && part.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%'
                }}
                title={part.description}
              >
                {part.description}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: isMobile ? 2 : 3,
              flex: '1 1 auto',
              justifyContent: 'flex-end',
              flexWrap: 'nowrap'
            }}
          >
            {part.companyName && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 'auto',
                  flexShrink: 1
                }}
              >
                <BusinessIcon
                  sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }}
                />
                <Typography variant="body2" color="text.secondary" noWrap>
                  {part.companyName}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 'auto',
                flexShrink: 1
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t('qty')}:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {part.lineQuantity || 1}
              </Typography>
            </Box>

            {!hidePricing && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 'auto',
                  flexShrink: 1
                }}
              >
                <AttachMoneyIcon
                  sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }}
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrency(part.lineCost)}
                </Typography>
              </Box>
            )}

            {!hidePricing && !isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 'auto',
                  flexShrink: 1
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('total')}:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(part.lineTotalCost)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
